import {
  GetStackFramesForRunKeyResponse,
  StackFrameType,
} from '@cotera/client/app/etc/api/typed-client';
import {
  CodeBlock,
  DisplayError,
  Icon,
  NotFound,
} from '@cotera/client/app/components';
import { Text } from '@cotera/client/app/components/ui/text';
import { joinElements } from '@cotera/client/app/components/utils';
import { useDataForRunKey } from '@cotera/client/app/hooks/use-stackframe-data';
import { DG } from '@cotera/sdk/core';
import { Suspense, useMemo, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import {
  ModelScope,
  ReadonlyWatchable,
  TargetableModel,
  useInScopeModel,
  useWatchableValue,
  Watchable,
} from '../actions/framework';
import { AppTab } from '../components/tab/tab';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../components/ui/breadcrumb';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from '../components/ui/resizable';
import { Skeleton } from '../components/ui/skeleton';
import { ExecutionFlowDisplay } from '../components/execution-flow-display';
import { GraphModel } from '../graph/graph.model';
import { getRunStatusClassName } from '../lib/utils';
import { TabLink, useActiveTab, useTabParams } from '../tab-manager';
import { makeBreadCrumbs } from './breadcrumbs.utils';
import { RunTraceChart } from './components';
import { v4 } from 'uuid';

// Section component for consistent spacing
const Section: React.FC<{
  title: string;
  children: React.ReactNode;
  className?: string;
  collapsible?: boolean;
  defaultOpen?: boolean;
  preview?: React.ReactNode;
  caption?: React.ReactNode;
}> = ({
  title,
  children,
  className = '',
  caption,
  collapsible = false,
  defaultOpen = true,
  preview,
}) => {
  if (!collapsible) {
    return (
      <div className={`space-y-3 ${className}`}>
        <div className="flex flex-col">
          <h3 className="text-sm font-medium text-foreground">{title}</h3>
          {caption !== undefined && (
            <span className="text-sm text-muted-foreground">{caption}</span>
          )}
        </div>
        {children}
      </div>
    );
  }

  return (
    <Collapsible defaultOpen={defaultOpen} className={`space-y-3 ${className}`}>
      <CollapsibleTrigger asChild>
        <div className="flex items-center justify-between w-full">
          <h3 className="text-sm font-medium text-foreground cursor-pointer hover:text-foreground/80 transition-colors flex items-center gap-2">
            {title}
            <Icon
              icon="chevron-down"
              className="h-3 w-3 transition-transform data-[state=open]:rotate-180"
            />
          </h3>
          {caption !== undefined && (
            <span className="text-sm text-muted-foreground">{caption}</span>
          )}
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>{children}</CollapsibleContent>
      {preview !== undefined && (
        <div className="text-sm text-muted-foreground bg-muted/50 p-2 rounded-md border border-border">
          {preview}
        </div>
      )}
    </Collapsible>
  );
};

class RunModel implements TargetableModel {
  readonly id: string;
  readonly changes: ReadonlyWatchable<Record<string, string | boolean>>;
  readonly pageTitle: ReadonlyWatchable<string>;

  constructor(
    readonly params: {
      graph: ReadonlyWatchable<DG.BuildExplorer>;
      key: string;
    }
  ) {
    this.id = `run-${params.key}`;
    this.changes = Watchable.fromValue({});
    this.pageTitle = Watchable.fromValue('Run Details');
  }

  isTargeted = false;
  target = () => {
    this.isTargeted = true;
  };
  markAsNotTargeted = () => {
    this.isTargeted = false;
  };
}

export const RunPage = () => {
  const { runKey } = useTabParams<{
    runKey: string;
  }>();

  const graphModel = useInScopeModel(GraphModel);
  const model = useMemo(
    () => {
      return new RunModel({
        graph: graphModel.graph,
        key: runKey,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [graphModel.graph]
  );

  // Generate fetchKey here, OUTSIDE the Suspense boundary.
  // When useSuspenseQuery suspends, React unmounts the child component.
  // If fetchKey is inside the child (MaybeRunContent), each remount creates
  // a new fetchKey -> new query -> new suspend -> infinite loop.
  // By lifting fetchKey here, it persists across suspend/resume cycles.
  const [fetchKey] = useState(() => v4());

  return (
    <AppTab model={model}>
      <ModelScope models={[model]}>
        <div className="flex flex-col gap-4 flex-1 h-full overflow-hidden">
          <ErrorBoundary
            fallbackRender={({ error }) => <DisplayError error={error} />}
          >
            <Suspense fallback={<RunPageSkeleton />}>
              <MaybeRunContent runKey={runKey} fetchKey={fetchKey} />
            </Suspense>
          </ErrorBoundary>
        </div>
      </ModelScope>
    </AppTab>
  );
};

const RunPageSkeleton = () => {
  return (
    <div className="h-full py-4 flex flex-col gap-4">
      {/* Breadcrumb Skeleton */}
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <div className="flex items-center gap-2">
                <Icon icon="clock" />
                <Skeleton className="h-4 w-24" />
              </div>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>
              <Skeleton className="h-4 w-32" />
            </BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <ResizablePanelGroup direction="horizontal" className="flex-1 min-h-0">
        {/* Main Content Panel */}
        <ResizablePanel defaultSize={50} minSize={30}>
          <div className="gap-6 flex flex-col pr-4 overflow-hidden h-full">
            {/* Header */}
            <div className="space-y-3 mt-2 flex-shrink-0">
              <div className="flex items-start justify-between">
                <div className="space-y-2 flex-1">
                  <Skeleton className="h-7 w-48" />
                </div>
                <div className="flex items-center gap-2">
                  <Skeleton className="h-9 w-20" />
                </div>
              </div>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto space-y-6">
              {/* Definition and Inputs */}
              <div className="flex gap-6">
                <div className="flex-1">
                  <div className="space-y-3">
                    <Skeleton className="h-5 w-20" />
                    <div className="border border-border rounded-lg p-4 bg-muted/20">
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-3/4 mt-2" />
                    </div>
                  </div>
                </div>
                <div className="flex-1">
                  <div className="space-y-3">
                    <Skeleton className="h-5 w-16" />
                    <div className="border border-border rounded-lg p-4 bg-muted/20">
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-2/3 mt-2" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Outputs */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Skeleton className="h-5 w-20" />
                  <Skeleton className="h-4 w-4" />
                  <Skeleton className="h-4 w-32" />
                </div>
                <div className="border border-border rounded-lg p-4 bg-muted/20">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4 mt-2" />
                </div>
              </div>

              {/* Sub Processes */}
              <div className="space-y-3">
                <Skeleton className="h-5 w-28" />
                <div className="space-y-2">
                  <div className="border border-border rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <div className="space-y-2">
                        <Skeleton className="h-4 w-32" />
                        <Skeleton className="h-3 w-48" />
                      </div>
                      <div className="flex items-center gap-2">
                        <Skeleton className="h-5 w-16 rounded-full" />
                        <Skeleton className="h-4 w-20" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Execution Timeline */}
              <div className="space-y-3">
                <div className="space-y-1">
                  <Skeleton className="h-5 w-36" />
                  <Skeleton className="h-4 w-80" />
                </div>
                <div className="border border-border rounded-lg p-4 bg-muted/20">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <Skeleton className="h-4 w-16" />
                      <Skeleton className="h-4 w-20" />
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Skeleton className="h-3 w-3 rounded-full" />
                        <Skeleton className="h-4 w-24" />
                        <Skeleton className="h-4 w-16" />
                      </div>
                      <div className="flex items-center gap-2">
                        <Skeleton className="h-3 w-3 rounded-full" />
                        <Skeleton className="h-4 w-32" />
                        <Skeleton className="h-4 w-16" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ResizablePanel>

        <ResizableHandle />

        {/* Right Sidebar Panel */}
        <ResizablePanel defaultSize={50} minSize={30}>
          <div className="border-l border-border pl-4 overflow-y-auto h-full">
            <div className="space-y-6">
              <div className="space-y-3">
                <Skeleton className="h-5 w-20" />

                {/* Status */}
                <div className="space-y-2">
                  <Skeleton className="h-3 w-12" />
                  <div className="flex items-center gap-2 p-2 border border-border rounded-md">
                    <Skeleton className="h-3 w-3 rounded-full" />
                    <Skeleton className="h-4 w-20" />
                  </div>
                </div>

                {/* Tags */}
                <div className="space-y-2">
                  <Skeleton className="h-3 w-12" />
                  <div className="flex flex-wrap gap-1">
                    <Skeleton className="h-5 w-16 rounded-full" />
                    <Skeleton className="h-5 w-20 rounded-full" />
                  </div>
                </div>

                {/* Execution Details */}
                <div className="space-y-2">
                  <Skeleton className="h-3 w-20" />
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <Skeleton className="h-4 w-16" />
                      <Skeleton className="h-4 w-20" />
                    </div>
                    <div className="flex justify-between">
                      <Skeleton className="h-4 w-18" />
                      <Skeleton className="h-4 w-20" />
                    </div>
                    <div className="flex justify-between">
                      <Skeleton className="h-4 w-20" />
                      <Skeleton className="h-4 w-24" />
                    </div>
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-32" />
                      <div className="border border-border rounded-lg p-3 bg-muted/20">
                        <Skeleton className="h-4 w-full" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ResizablePanel>
      </ResizablePanelGroup>
    </div>
  );
};

const MaybeRunContent = ({
  runKey,
  fetchKey,
}: {
  runKey: string;
  fetchKey: string;
}) => {
  const activeTab = useActiveTab();
  const { tabId } = useTabParams();
  const { data: response } = useDataForRunKey({
    runKey,
    visible: activeTab === tabId,
    refetchFrequency: 400,
    fetchKey,
  });

  if (response.isErr()) {
    return <DisplayError error={response.error.err} />;
  }

  if (response.value === null) {
    return <NotFound resource="Run" />;
  }

  return <RunPageContent response={response.value} />;
};

// Helper function to find a frame by ID in flat array
const findFrameById = (
  frameId: string,
  frames: GetStackFramesForRunKeyResponse['frames']
): GetStackFramesForRunKeyResponse['frames'][0] | null => {
  return frames.find((frame) => frame.id === frameId) ?? null;
};

const RunPageContent = ({
  response,
}: {
  response: GetStackFramesForRunKeyResponse;
}) => {
  // Extract the root frame
  const rootFrame = response.frames[0];
  if (!rootFrame) {
    return <NotFound resource="Run" />;
  }

  return <RunPageFrameFound response={response} rootFrame={rootFrame} />;
};

const RunPageFrameFound = ({
  response,
  rootFrame,
}: {
  response: GetStackFramesForRunKeyResponse;
  rootFrame: StackFrameType;
}) => {
  const graphModel = useInScopeModel(GraphModel);
  const baseGraph = useWatchableValue(graphModel.graph);
  // State for selected frame (defaults to root)
  const [selectedFrameId, setSelectedFrameId] = useState<string>(rootFrame.id);

  // Find the selected frame
  const selectedFrame =
    findFrameById(selectedFrameId, response.frames) || rootFrame;

  const path = makeBreadCrumbs(response.runKey, baseGraph);

  return (
    <div className="h-full py-4 flex flex-col gap-2">
      {/* Breadcrumb */}
      <Breadcrumb>
        <BreadcrumbList>
          {joinElements(
            path.map((crumb, index) => {
              if (index === path.length - 1) {
                return (
                  <BreadcrumbItem key={index}>
                    <BreadcrumbPage>{crumb.display}</BreadcrumbPage>
                  </BreadcrumbItem>
                );
              }

              return (
                <BreadcrumbItem key={index}>
                  <BreadcrumbLink asChild>
                    {crumb.url !== undefined ? (
                      <TabLink to={crumb.url} type="replace">
                        {crumb.display}
                      </TabLink>
                    ) : (
                      <span>{crumb.display}</span>
                    )}
                  </BreadcrumbLink>
                </BreadcrumbItem>
              );
            }),
            (i) => (
              <BreadcrumbSeparator key={i} />
            )
          )}
        </BreadcrumbList>
      </Breadcrumb>

      <ResizablePanelGroup direction="horizontal" className="flex-1 min-h-0">
        {/* Main Content Panel */}
        <ResizablePanel defaultSize={50} minSize={30}>
          <div className="gap-4 flex flex-col pr-4 overflow-hidden min-w-0 h-full">
            {/* Header */}
            <div className="space-y-2 mt-2 flex-shrink-0">
              <div className="flex items-start justify-between">
                <div className="space-y-2 flex-1">
                  <h1 className="text-xl font-semibold text-foreground">
                    Job Run Details
                  </h1>
                  <Text.Caption>{response.runId}</Text.Caption>
                </div>
                {/* <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm">
                    <Icon icon="play" className="h-4 w-4" />
                    Rerun
                  </Button>
                </div> */}
              </div>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto overflow-x-hidden space-y-4">
              {/* Conversation / Chat History */}
              <Section
                key={`conversation-${selectedFrameId}`}
                title="Execution Flow"
                collapsible
                defaultOpen={true}
              >
                <ErrorBoundary
                  fallbackRender={({ error }) => (
                    <div className="flex flex-col items-center justify-center gap-2 py-8">
                      <span className="text-sm text-destructive">
                        {error.message || 'Failed to load conversation'}
                      </span>
                    </div>
                  )}
                >
                  <Suspense
                    fallback={
                      <div className="flex flex-col items-center justify-center gap-2 py-8">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                        <span className="text-sm text-muted-foreground">
                          Loading conversation…
                        </span>
                      </div>
                    }
                  >
                    <ExecutionFlowDisplay frames={response.frames} />
                  </Suspense>
                </ErrorBoundary>
              </Section>
            </div>
          </div>
        </ResizablePanel>

        <ResizableHandle />

        {/* Right Sidebar Panel - Frame Details */}
        <ResizablePanel defaultSize={50} minSize={30}>
          <div className="border-l border-border pl-4 overflow-y-auto h-full">
            <div className="space-y-4">
              {/* Execution Timeline - moved from left panel */}
              <Section
                title="Execution Timeline"
                caption={
                  <div className="flex items-center gap-1.5">
                    <div
                      className={`w-2 h-2 rounded-full ${getRunStatusClassName(
                        rootFrame.status
                      )}`}
                    />
                    <span className="text-xs capitalize">
                      {rootFrame.status}
                    </span>
                  </div>
                }
                collapsible
                defaultOpen={true}
              >
                <div className="space-y-3 min-w-0">
                  <RunTraceChart
                    runData={response.frames}
                    height={400}
                    showTimeline={true}
                    showDuration={true}
                    showSidebar={true}
                    enableNavigation={false}
                    onSpanClick={setSelectedFrameId}
                    className="border border-border rounded-lg overflow-hidden min-w-0"
                  />
                </div>
              </Section>

              {/* Inputs */}
              <Section
                key={`inputs-${selectedFrameId}`}
                title="Inputs"
                caption={
                  rootFrame.startedAt !== null
                    ? new Date(rootFrame.startedAt).toLocaleString('en-US', {
                        timeZone: 'UTC',
                        dateStyle: 'medium',
                        timeStyle: 'medium',
                      }) + ' UTC'
                    : undefined
                }
                collapsible
                defaultOpen={false}
              >
                <CodeBlock
                  code={JSON.stringify(selectedFrame.input, null, 2)}
                  language="json"
                  wrap={true}
                />
              </Section>

              {/* Outputs */}
              <Section
                key={`outputs-${selectedFrameId}`}
                title="Outputs"
                caption={`${selectedFrame.output?.['ty'] ?? ''}`}
                collapsible
                defaultOpen={false}
              >
                <CodeBlock
                  code={JSON.stringify(
                    selectedFrame.output?.['val'] ?? selectedFrame.output,
                    null,
                    2
                  )}
                  language="json"
                  wrap={true}
                />
              </Section>
            </div>
          </div>
        </ResizablePanel>
      </ResizablePanelGroup>
    </div>
  );
};
